/**
 * RunNavShell — the SHARED end-user "Run" navigation shell.
 *
 * The SAME component backs every Run surface:
 *   - the platform's /run/{tenant}/{project} view (designer LivePreview panes)
 *   - the deployed runtimes' /run/{tenant}/{project} view (runtime embeds),
 *     served by both the Python and the J2EE runtime server.
 *
 * One navigation model, seven skins: `useShellNav` (shell/nav) owns the tree,
 * the current screen, Pinned, Recent, Open editors, full screen and the
 * layout; the layout the user picked (shell/layouts) draws the chrome around
 * the screen. The screen itself is the host's `renderObject`, unchanged in
 * every layout.
 *
 * Everything host-specific stays outside and is injected:
 *   - `nav`             the flat navigation list (host fetches + rights-filters it)
 *   - `renderObject`    how a selected object renders
 *   - `headerExtras`    optional extra buttons in the screen title
 *   - `prefsTransport`  the server record for layout / Pinned / Recent
 *   - `shellConfig`     `application.shell` (default + allowed layouts, caps)
 *   - `appKey` / `userKey`  who and which application (local mirror, snapshots)
 *   - `editors`         Open editors on (the Run view)
 *
 * Styling uses the platform CSS variables WITH literal fallbacks
 * (`var(--color-x, #hex)`) so the shell renders in both hosts.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  BarChart3, Clock, Cog, Database, FileSignature, FileText, FolderArchive, FolderOpen, GitBranch,
  Globe, AppWindow, HardDrive, LayoutDashboard, LifeBuoy, PieChart, StickyNote, Wand2, Waypoints, Workflow,
} from 'lucide-react'
import { useActivityPageView } from './activity'
import { LAYOUTS } from './shell/nav/config'
import { usePublishShellChrome, type ShellChromeSlots } from './shell/nav/chromeContext'
import { useShellNav } from './shell/nav/useShellNav'
import type { AppShellSettings } from './shell/nav/config'
import type { ShellPrefsTransport } from './shell/nav/prefs'
import { CapNotice, ExitFullScreen, ExpiredEditorsDialog } from './shell/nav/parts/Notices'
import { EditorSessionHosts } from './shell/nav/ShellFrame'
import { S, SHELL_CSS } from './shell/nav/tokens'
import { LAYOUT_COMPONENTS, type LayoutTopBarSlots } from './shell/layouts'
import ClassicLayout from './shell/layouts/classic'
import type { RenderObjectContext, RunNavItem } from './shell/nav/types'

export type { RenderObjectContext, RunNavItem } from './shell/nav/types'
export { RUN_TYPE_LABEL } from './shell/nav/typeLabels'

// ─── Types ──────────────────────────────────────────────────────────

export interface RunNavShellProps {
  /** Flat navigation list (folders '-' + objects). */
  nav: RunNavItem[]
  /** Fallback root parent id when no node has an empty parent_id (legacy
   *  application.json where roots point at the application id). */
  rootParentId?: string
  /** App title for the empty pane. */
  appTitle: string
  /** Accent color (hex or css var expression). */
  themeColor?: string
  /** Compact mode — smaller header/nav (split-pane embeds). */
  compact?: boolean
  /**
   * Controlled selection: the obj_name that should be open, or null for none.
   * Pass `undefined` to let the shell manage selection internally (builder
   * embeds that must not touch the URL). When controlled, clicks call
   * `onSelect` and the shell re-derives the open object from this prop.
   */
  selectedObjName?: string | null
  /** Selection change (item, or null on close). Hosts update the URL and/or
   *  observe the open object here. */
  onSelect?: (item: RunNavItem | null, meta?: { auto?: boolean }) => void
  /** Content pane for an object — the host-specific renderer. `ctx.background`
   *  marks a copy that is not the URL's screen (a second desktop window, an
   *  Open editor's kept-alive instance): give it no URL in-screen state. */
  renderObject: (item: RunNavItem, ctx?: RenderObjectContext) => ReactNode
  /** Optional extra header buttons for the open object (e.g. builder refresh). */
  headerExtras?: (item: RunNavItem) => ReactNode
  /** Retained for compatibility. The shell's menus never carry a search box:
   *  search is the host's global search, opened from the top bar. */
  hideNavSearch?: boolean
  /** Message when the nav tree is empty. */
  emptyNavMessage?: string
  /** Message in the object pane when nothing is selected. */
  emptyPaneMessage?: string
  /** Server record for layout / Pinned / Recent (user memory). Absent ⇒ local only. */
  prefsTransport?: ShellPrefsTransport
  /** `application.shell` — default + allowed layouts, caps. */
  shellConfig?: AppShellSettings | null
  /** The application (owner/tenant/project) and the user — keys for the local
   *  mirror and the open-editor snapshots. */
  appKey?: string
  userKey?: string
  /** Open editors (minimisable Add / Edit sessions). Off ⇒ screens open their
   *  editors themselves, exactly as before (builder preview). */
  editors?: boolean
  /** The host's session ended — the shell asks about unsaved editors. */
  sessionExpired?: boolean
  /** "Sign in again" from that question. */
  onSignInAgain?: () => void
  /** Restrict to the classic layout (builder preview panes). */
  layoutLocked?: boolean
}

// ─── Shared look-up tables ─────────────────────────────────────────

/** Type → icon component. Kept for hosts that draw a type icon of their own;
 *  menu entries use `NavIcon` (shell/nav/navIcons), which honours a node's
 *  own `icon` first. */
export const RUN_TYPE_ICON: Record<string, typeof FolderOpen> = {
  '-': FolderOpen,
  T: FileText,
  D: LayoutDashboard,
  V: PieChart,
  M: Database,
  R: BarChart3,
  G: Cog,
  W: GitBranch,
  L: Waypoints,
  P: HardDrive,
  S: StickyNote,
  X: Globe,
  U: Wand2,
  C: AppWindow,
  K: FolderArchive,
  H: Clock,
  J: Workflow,
  E: FileSignature,
  SUPPORT: LifeBuoy,
}

// ─── Shell ─────────────────────────────────────────────────────────

export function RunNavShell({
  nav,
  rootParentId,
  appTitle,
  themeColor,
  compact = false,
  selectedObjName,
  onSelect,
  renderObject,
  headerExtras,
  emptyNavMessage = 'No navigation items defined.',
  emptyPaneMessage = 'Pick a screen from the menu to open it.',
  prefsTransport,
  shellConfig,
  appKey,
  userKey,
  editors = false,
  sessionExpired = false,
  onSignInAgain,
  layoutLocked = false,
}: RunNavShellProps) {
  // The record editor, lookups and child forms are portaled to <body>, out
  // of reach of the shell's own `--app-theme-color` — so the page root
  // carries it too while this shell is up (radios, check boxes, Yes / No
  // switches inside those windows take the project's colour).
  useEffect(() => {
    if (!themeColor || typeof document === 'undefined') return
    const root = document.documentElement
    const prev = root.style.getPropertyValue('--app-theme-color')
    root.style.setProperty('--app-theme-color', themeColor)
    return () => {
      if (prev) root.style.setProperty('--app-theme-color', prev)
      else root.style.removeProperty('--app-theme-color')
    }
  }, [themeColor])
  // While a RUNNING application owns the page, its primary buttons wear the
  // application's colour too (appTheme / globals.css `html[data-tw-app-theme]
  // .btn-primary`). Not in the builder's preview pane (`layoutLocked`): there
  // the page around it is the builder, whose buttons stay the platform's.
  // Only a colour the application declares: a host's fallback (a `var(…)`
  // of the platform accent) leaves the platform look as it was.
  const paintsButtons = !!themeColor && !/^\s*var\(/.test(themeColor) && !layoutLocked
  useEffect(() => {
    if (!paintsButtons || typeof document === 'undefined') return undefined
    const root = document.documentElement
    root.setAttribute('data-tw-app-theme', '')
    return () => root.removeAttribute('data-tw-app-theme')
  }, [paintsButtons])

  const model = useShellNav({
    nav, rootParentId, selectedObjName, onSelect, prefsTransport, shellConfig,
    appKey, userKey, editors, sessionExpired,
  })

  // Activity analytics: one screen view per object opened — whichever way it
  // was opened (a click, a deep link, a reload, a smartpage navigation).
  const current = model.current
  useActivityPageView(current?.obj_name, current
    ? { obj_type: current.obj_type, title: current.title }
    : undefined)

  // What the active layout puts in the host's top bar, merged with what the
  // shell itself owns there (full screen, the layout menu, the sign-out guard).
  // Each layout clears its own slots on unmount (its cleanup runs before the
  // next layout publishes). Identical publications are dropped, so a layout
  // re-publishing on every render cannot loop through this state.
  const [layoutSlots, setLayoutSlots] = useState<LayoutTopBarSlots | null>(null)
  const setTopBarSlots = useCallback((s: LayoutTopBarSlots | null) => setLayoutSlots((prev) => (
    prev === s || (!!prev && !!s && prev.left === s.left && prev.center === s.center && prev.right === s.right)
      ? prev : s)), [])
  const layoutId = layoutLocked ? 'classic' : model.effectiveLayout
  const { setLayout, confirmSignOut, afterSignOut } = model
  const allowed = model.config.allowedLayouts
  const layoutOptions = useMemo(() => LAYOUTS.filter((l) => allowed.includes(l.id)), [allowed])
  const slots = useMemo<ShellChromeSlots>(() => ({
    ...(layoutSlots || {}),
    hidden: model.fullScreen,
    layout: !layoutLocked && layoutOptions.length > 1
      ? { value: model.layout, options: layoutOptions, onChange: setLayout }
      : undefined,
    beforeSignOut: confirmSignOut,
    afterSignOut,
  }), [layoutSlots, model.fullScreen, layoutLocked, layoutOptions, model.layout, setLayout, confirmSignOut, afterSignOut])
  usePublishShellChrome(slots)

  const Layout = LAYOUT_COMPONENTS[layoutId] || ClassicLayout

  return (
    <div
      // The Run shell is on screen: a signed-in user is INSIDE the
      // application (an empty menu then means "no grants", not "signed out").
      data-tw-shell="run"
      data-tw-shell-layout={layoutId}
      style={{
        // The project's theme colour, for controls inside the screens this
        // shell hosts (a form's Yes / No switch) — they never see the prop.
        ...(themeColor ? { '--app-theme-color': themeColor } as React.CSSProperties : {}),
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: S.bg,
        color: S.text,
        minHeight: 0,
        position: model.fullScreen ? 'fixed' : 'relative',
        ...(model.fullScreen ? { inset: 0, zIndex: 9000 } : {}),
        // The platform inherits this from globals.css; the standalone runtime
        // has no global stylesheet, so the shell carries its own font stack.
        fontFamily: S.font,
      }}
    >
      <style>{SHELL_CSS}</style>
      {/* Full screen: a slim bar above the screen (title + Exit), in the flow. */}
      <ExitFullScreen model={model} />
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <Layout
          model={model}
          appTitle={appTitle}
          themeColor={themeColor}
          compact={compact}
          renderObject={renderObject}
          headerExtras={headerExtras}
          emptyNavMessage={emptyNavMessage}
          emptyPaneMessage={emptyPaneMessage}
          setTopBarSlots={setTopBarSlots}
        />
      </div>
      {/* Open editors' kept-alive instances — here, not inside the layout, so a
          layout switch keeps every half-filled record exactly as it was. */}
      <EditorSessionHosts model={model} renderObject={renderObject} />
      <CapNotice model={model} onOpenList={model.openEditorsList} />
      <ExpiredEditorsDialog model={model} onSignIn={onSignInAgain} />
    </div>
  )
}
